import { createAction, createAsyncThunk } from "@reduxjs/toolkit"

import { getPendingContracts } from "../../containers/Contracts/api/services.ts"
import type { ContractListItem } from "../../containers/Contracts/types/types.ts"
import {
  getUnreadNotificationsByType,
  markNotificationRead
} from "../../containers/Notifications/api/services.ts"
import type { Notification } from "../../containers/Notifications/types/notification.types.ts"
import { getMyPendingReviews } from "../../containers/Project/api/project-attachment-workflow.services.ts"
import { getPendingProposals } from "../../containers/Proposals/api/services.ts"
import type { ProposalListItem } from "../../containers/Proposals/types/types.ts"
import type { RootState } from "../../store.ts"
import { NOTIFICATION_TYPE_ID } from "./notification-counts.constants.ts"
import type {
  ApprovalCounts,
  NewEventItem,
  NewProjectItem
} from "./notification-counts.types.ts"

// The Home card names the actual new item(s), not just a count — but never
// the whole backlog. Three is enough to read "Sunset Residence, Maple Street
// Townhomes, +1 more" without the card growing into a feed.
const MAX_ITEMS_KEPT = 3

interface NotificationCountsResult {
  projects: number
  projectItems: NewProjectItem[]
  calendar: number
  calendarItems: NewEventItem[]
  approvals: ApprovalCounts
}

const toProjectItem = (n: Notification): NewProjectItem | null =>
  n.data?.project_id
    ? {
        id: n.data.project_id,
        name: n.data.project_name || "Untitled project",
        createdAt: n.timestamp
      }
    : null

const toEventItem = (n: Notification): NewEventItem | null =>
  n.data?.event_id
    ? {
        id: n.data.event_id,
        description: n.description,
        startAt: n.data.start_at ?? null
      }
    : null

const toProposalItem = (p: ProposalListItem) => ({
  id: p.id,
  label: p.customer_name ? `${p.proposal_id} · ${p.customer_name}` : p.proposal_id
})

const toContractItem = (c: ContractListItem) => ({
  id: c.id,
  label: c.customer_name ? `${c.contract_id} · ${c.customer_name}` : c.contract_id
})

// Every source here is independent and already permission/visibility-scoped
// server-side (see docs/kb entries for EAD-615 Phase 7/9). A single source
// failing (e.g. a transient 500 on one endpoint) should not blank out the
// other badges, so each is read defensively rather than through the shared
// rejectWithHandledError path — this is a soft, non-critical indicator, not
// a page the user is waiting on.
async function readSafely<T>(promise: Promise<{ ok: boolean; data?: unknown }>, fallback: T, extract: (data: unknown) => T): Promise<T> {
  try {
    const response = await promise
    if (!response.ok) return fallback
    return extract(response.data) ?? fallback
  } catch {
    return fallback
  }
}

// Each Approval source is also gated server-side, but the endpoint still
// answers for a user with no page for it — calling it anyway would send a
// request for a badge/row the sidebar never renders. `request` is a factory
// (not an already-started call) so the network call itself is skipped, not
// just its result, when the page isn't granted — the same source SidebarBody
// itself reads to decide whether to show the row.
const readWhenPermitted = <T>(
  request: () => Promise<{ ok: boolean; data?: unknown }>,
  hasPage: boolean,
  fallback: T,
  extract: (data: unknown) => T
): Promise<T> => (hasPage ? readSafely(request(), fallback, extract) : Promise.resolve(fallback))

export const fetchNotificationCounts = createAsyncThunk<NotificationCountsResult>(
  "notificationCounts/fetchNotificationCounts",
  async (_, { getState }) => {
    const pages = (getState() as RootState).auth?.permissions?.pages || []
    const [
      { count: projects, items: projectItems },
      { count: calendar, items: calendarItems },
      proposalRows,
      contractRows,
      documents
    ] = await Promise.all([
      readSafely(
        getUnreadNotificationsByType(NOTIFICATION_TYPE_ID.PROJECT_CREATED),
        { count: 0, items: [] as NewProjectItem[] },
        (data) => {
          const page = data as { count: number; results: Notification[] }
          return {
            count: page.count,
            items: page.results
              .map(toProjectItem)
              .filter((item): item is NewProjectItem => item !== null)
              .slice(0, MAX_ITEMS_KEPT)
          }
        }
      ),
      readSafely(
        getUnreadNotificationsByType(NOTIFICATION_TYPE_ID.CALENDAR_EVENT_ASSIGNED),
        { count: 0, items: [] as NewEventItem[] },
        (data) => {
          const page = data as { count: number; results: Notification[] }
          return {
            count: page.count,
            items: page.results
              .map(toEventItem)
              .filter((item): item is NewEventItem => item !== null)
              .slice(0, MAX_ITEMS_KEPT)
          }
        }
      ),
      readWhenPermitted(getPendingProposals, pages.includes("proposals"), [] as ProposalListItem[], (data) =>
        Array.isArray(data) ? (data as ProposalListItem[]) : []
      ),
      readWhenPermitted(getPendingContracts, pages.includes("contracts"), [] as ContractListItem[], (data) =>
        Array.isArray(data) ? (data as ContractListItem[]) : []
      ),
      readWhenPermitted(
        getMyPendingReviews,
        pages.includes("document_reviews"),
        0,
        (data) => (data as { count: number })?.count ?? 0
      )
    ])

    return {
      projects,
      projectItems,
      calendar,
      calendarItems,
      approvals: {
        proposals: proposalRows.length,
        proposalItems: proposalRows.slice(0, MAX_ITEMS_KEPT).map(toProposalItem),
        contracts: contractRows.length,
        contractItems: contractRows.slice(0, MAX_ITEMS_KEPT).map(toContractItem),
        documents,
        total: proposalRows.length + contractRows.length + documents
      }
    }
  }
)

// Marks every currently-unread notification of one type as read, the same
// way opening it from the bell already does (POST .../read/ per id) — there
// is no bulk-by-type endpoint, and counts here are small (a handful of new
// projects/events), so one call per id is the smallest correct option.
const markAllOfTypeRead = async (notificationType: number): Promise<void> => {
  const response = await getUnreadNotificationsByType(notificationType)
  if (!response.ok || !response.data?.results?.length) return

  await Promise.all(
    response.data.results.map((notification) =>
      markNotificationRead(notification.id)
    )
  )
}

// Marking one category read only changes that category — the other four
// sources are untouched, so clearing it locally is both correct and cheaper
// than re-fetching every source on every Projects/Calendar visit.
export const projectNotificationsCleared = createAction(
  "notificationCounts/projectNotificationsCleared"
)
export const calendarNotificationsCleared = createAction(
  "notificationCounts/calendarNotificationsCleared"
)

export const markProjectNotificationsSeen = createAsyncThunk(
  "notificationCounts/markProjectNotificationsSeen",
  async (_: void, { dispatch }) => {
    await markAllOfTypeRead(NOTIFICATION_TYPE_ID.PROJECT_CREATED)
    dispatch(projectNotificationsCleared())
  }
)

export const markCalendarNotificationsSeen = createAsyncThunk(
  "notificationCounts/markCalendarNotificationsSeen",
  async (_: void, { dispatch }) => {
    await markAllOfTypeRead(NOTIFICATION_TYPE_ID.CALENDAR_EVENT_ASSIGNED)
    dispatch(calendarNotificationsCleared())
  }
)
